import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import { Wordmark } from "@/components/Wordmark";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useLang, LANGS } from "@/i18n/translations";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";

const LanguageToggle = ({ className = "", idSuffix = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div data-testid={`navbar-language-toggle${idSuffix}`} className={`inline-flex items-center rounded-full border border-white/10 bg-white/5 p-0.5 ${className}`}>
      {LANGS.map((l) => (
        <button
          key={l}
          data-testid={`language-toggle${idSuffix}-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1 rounded-full text-[11px] font-mono-cs uppercase transition-[background-color,color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            lang === l ? "bg-[#00d4ff] text-[#05070d] font-medium" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const { scrollTo } = useSmoothScroll();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const items = [
    { label: t.nav.references, target: "#referenzen" },
    { label: t.nav.services, target: "#leistungen" },
    { label: t.nav.industries, target: "#branchen" },
    { label: t.nav.process, target: "#prozess" },
    { label: t.nav.contact, target: "#kontakt" },
  ];

  const go = (target) => {
    setOpen(false);
    // Hardening: Radix/react-remove-scroll sperrt body waehrend das Sheet offen
    // ist. Wird waehrend der Schliess-Animation navigiert (Navbar unmountet),
    // koennen pointer-events/overflow-Locks haengen bleiben -> Seite wirkt tot.
    window.setTimeout(() => {
      document.body.style.removeProperty("pointer-events");
      document.body.style.removeProperty("overflow");
    }, 400);
    if (location.pathname !== "/") {
      navigate("/");
      // Home runs ScrollTrigger.refresh() ~600ms after mount; scroll after it.
      setTimeout(() => scrollTo(target), 800);
    } else {
      scrollTo(target);
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 border-b ${
        scrolled ? "bg-[#05070d]/75 backdrop-blur-md border-white/10" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
        <Wordmark className="text-[17px]" />

        <nav className="hidden lg:flex items-center gap-1" aria-label="Hauptnavigation">
          {items.map((item) => (
            <button
              key={item.target}
              data-testid={`navbar-link-${item.target.slice(1)}`}
              onClick={() => go(item.target)}
              className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageToggle />
          <Button
            data-testid="navbar-start-project-button"
            onClick={() => go("#kontakt")}
            className="bg-[#00d4ff] text-[#05070d] hover:bg-[#33ddff] font-medium rounded-full px-5 shadow-[0_0_24px_rgba(0,212,255,0.18)] transition-[background-color,box-shadow,transform] duration-200 hover:-translate-y-px"
          >
            {t.nav.startProject}
          </Button>
        </div>

        <div className="flex lg:hidden items-center gap-2">
          <LanguageToggle idSuffix="-mobile" />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="navbar-mobile-menu-button"
                aria-label="Menü öffnen"
                className="p-2 rounded-md text-foreground hover:bg-white/5 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#070a12] border-white/10 w-[280px]">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <div className="mt-8 flex flex-col gap-1">
                {items.map((item) => (
                  <button
                    key={item.target}
                    data-testid={`navbar-mobile-link-${item.target.slice(1)}`}
                    onClick={() => go(item.target)}
                    className="text-left px-3 py-3 text-base text-foreground/90 hover:bg-white/5 rounded-lg transition-colors duration-200"
                  >
                    {item.label}
                  </button>
                ))}
                <Button
                  data-testid="navbar-mobile-start-project-button"
                  onClick={() => go("#kontakt")}
                  className="mt-4 bg-[#00d4ff] text-[#05070d] hover:bg-[#33ddff] font-medium rounded-full"
                >
                  {t.nav.startProject}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
